<script>
  import { onMount } from 'svelte'
  import { get } from 'svelte/store'
  import { currentTab, currentView, syncStatus, pendingSyncCount, recentWorkouts, navigateTo, goHome } from './stores/app.js'
  import { auth, isLoggedIn } from './stores/auth.js'
  import { initDB, getWorkouts, deleteWorkout, initExerciseLibrary, getSyncQueue } from './services/db.js'
  import { setupAutoSync, onSyncStatusChange, performFullSync, getPendingSyncCount } from './services/sync.js'
  import { registerServiceWorker } from './services/sw-register.js'

  import Home from './components/Home.svelte'
  import WorkoutForm from './components/WorkoutForm.svelte'
  import WorkoutList from './components/WorkoutList.svelte'
  import ExerciseManager from './components/ExerciseManager.svelte'
  import BottomNavigation from './components/BottomNavigation.svelte'
  import Overview from './components/Overview.svelte'
  import Database from './components/Database.svelte'
  import TopBar from './components/TopBar.svelte'
  import Login from './components/Login.svelte'
  import ImportPage from './components/ImportPage.svelte'
  import UserManagement from './components/UserManagement.svelte'

  let isInitialized = false
  let initError = null

  onMount(async () => {
    try {
      // Initialize IndexedDB first (required by auth for exercise library)
      await initDB()
      console.log('IndexedDB initialized')

      // Initialize auth state
      await auth.init()
      console.log('Auth initialized')

      // Initialize exercise library (this is a no-op now)
      await initExerciseLibrary()
      console.log('Exercise library initialized')

      // Load initial data in parallel
      await Promise.all([
        loadRecentWorkouts(),
        updatePendingCount()
      ])

      // Set initialized early - UI can now be shown with offline data
      isInitialized = true
      console.log('Wild Train - Ready!')

      // Update online status
      syncStatus.update(s => ({ ...s, isOnline: navigator.onLine }))

      // Register Service Worker in background (non-blocking)
      if ('serviceWorker' in navigator) {
        registerServiceWorker().then(async () => {
          console.log('Service Worker registered')

          // Send API URL to Service Worker
          const registration = await navigator.serviceWorker.ready
          if (registration.active) {
            registration.active.postMessage({
              type: 'SET_API_URL',
              url: import.meta.env.VITE_GAS_WEB_APP_URL
            })
          }
        }).catch(err => {
          console.warn('Service Worker registration failed:', err)
        })
      }

      // Listen for sync status changes (before setupAutoSync)
      onSyncStatusChange((status) => {
        // Merge with current status to preserve isOnline
        syncStatus.update(s => ({ ...s, ...status, isOnline: navigator.onLine }))
      })

      // Setup auto sync (without initial sync - will run after login)
      setupAutoSync(false)

      // Listen for messages from Service Worker
      navigator.serviceWorker?.addEventListener('message', async (event) => {
        if (event.data.type === 'SYNC_COMPLETE') {
          console.log(`Background sync completed: ${event.data.count} items`)
          await loadRecentWorkouts()
        }
      })

      console.log('VITE_GAS_WEB_APP_URL:', import.meta.env.VITE_GAS_WEB_APP_URL)

      // Trigger initial full sync if already logged in
      const isUserLoggedIn = get(isLoggedIn)
      if (isUserLoggedIn && navigator.onLine) {
        setTimeout(async () => {
          console.log('Starting initial full sync...')
          await performFullSync()
          await loadRecentWorkouts()
          await updatePendingCount()
        }, 1000)
      }
    } catch (error) {
      console.error('Initialization error:', error)
      initError = error
    }
  })

  async function loadRecentWorkouts() {
    try {
      const workouts = await getWorkouts()
      recentWorkouts.set(workouts.slice(0, 5))
    } catch (error) {
      console.error('Failed to load workouts:', error)
    }
  }

  async function updatePendingCount() {
    try {
      const count = await getPendingSyncCount()
      pendingSyncCount.set(count)
    } catch (error) {
      console.error('Failed to get pending count:', error)
    }
  }

  async function handleSync() {
    try {
      const result = await performFullSync()
      if (result.success) {
        console.log(`Sync complete: uploaded ${result.uploaded || 0}, downloaded ${result.downloaded || 0}`)
      } else {
        console.warn('Sync failed:', result.error)
      }
    } finally {
      await updatePendingCount()
      await loadRecentWorkouts()
    }
  }

  async function handleDeleteWorkout(event) {
    const { workoutId } = event.detail
    await deleteWorkout(workoutId)
    await loadRecentWorkouts()
  }

  function handleNavigate(event) {
    const { view, options } = event.detail
    navigateTo(view, options)
  }

  // Listen for online/offline events
  function handleOnline() {
    syncStatus.update(s => ({ ...s, isOnline: true }))
  }

  function handleOffline() {
    syncStatus.update(s => ({ ...s, isOnline: false }))
  }
</script>

<svelte:window on:online={handleOnline} on:offline={handleOffline} />

{#if initError}
  <div class="container mx-auto px-4 py-8">
    <div class="card bg-red-50 border border-red-200">
      <h2 class="text-xl font-semibold text-red-800 mb-2">初始化失敗</h2>
      <p class="text-red-700">{initError.message}</p>
      <button on:click={() => location.reload()} class="btn btn-primary mt-4">
        重新載入
      </button>
    </div>
  </div>
{:else if !isInitialized}
  <div class="container mx-auto px-4 py-8">
    <div class="text-center">
      <div class="spinner mx-auto mb-4"></div>
      <p class="text-gray-600">載入中...</p>
    </div>
  </div>
{:else if !$isLoggedIn}
  <!-- Login page -->
  <Login on:loginSuccess={async () => {
    // Refresh data after login
    await loadRecentWorkouts()
    await updatePendingCount()

    // Trigger initial full sync after login
    if (navigator.onLine) {
      setTimeout(async () => {
        console.log('Starting initial full sync after login...')
        await performFullSync()
        await loadRecentWorkouts()
        await updatePendingCount()
      }, 1000)
    }
  }} />
{:else}
  <div class="flex h-dvh">
    <!-- Main content area with bottom padding for navigation -->
    <div class="pb-20 flex-1">
      <!-- Top Bar -->
      <TopBar on:sync={handleSync} on:navigate={handleNavigate} />

      {#if $currentView === 'home'}
        <!-- Tab-based content -->
        {#if $currentTab === 'workout'}
          <Home
            on:navigate={handleNavigate}
            on:delete={handleDeleteWorkout}
          />
        {:else if $currentTab === 'overview'}
          <Overview />
        {:else if $currentTab === 'database'}
          <Database />
        {/if}
      {:else if $currentView === 'workoutForm'}
        <WorkoutForm
          on:close={goHome}
          on:save={async () => {
            await loadRecentWorkouts()
            goHome()
          }}
        />
      {:else if $currentView === 'workoutList'}
        <WorkoutList
          on:close={goHome}
          on:edit={(e) => navigateTo('workoutForm', { mode: 'edit', workoutId: e.detail.workoutId })}
          on:delete={handleDeleteWorkout}
        />
      {:else if $currentView === 'exerciseManager'}
        <ExerciseManager on:close={goHome} />
      {:else if $currentView === 'import'}
        <ImportPage
          on:close={goHome}
          on:importSuccess={async () => {
            await handleSync()
            await loadRecentWorkouts()
          }}
        />
      {:else if $currentView === 'userManagement'}
        <UserManagement on:close={goHome} />
      {/if}
    </div>

    <!-- Bottom Navigation -->
    <BottomNavigation />
  </div>
{/if}
